import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import SubHeader from "components/common/SubHeader";
import OrderCard from "./OrderCard";
import ReviewWriteBottomSheet from "components/review/ReviewWriteBottomSheet";
import { getMyOrderHistory, cancelOrder, confirmPurchase } from "api/orderHistory";
import { useSnackbar } from "contexts/SnackbarContext";
import LoadingSpinner from "components/common/LoadingSpiner";
import useRefreshCallback from "hooks/useRefreshCallback";

import "./orderHistory.scss";

// next URL(`...?page=2`)에서 page 번호만 추출한다.
const extractPageFromUrl = (url) => {
    if (!url) return null;
    try {
        const u = new URL(url);
        const page = u.searchParams.get("page");
        return page ? Number(page) : null;
    } catch {
        return null;
    }
};

// 주문 카드 가상화 래퍼.
// 화면(+상하 600px 버퍼) 안에 들어온 카드만 OrderCard 를 마운트하고,
// 벗어난 카드는 마지막으로 측정한 높이를 가진 placeholder 만 남겨둔다.
// 카드 안의 <img> 도 함께 언마운트되므로 무한스크롤 누적으로 인한 메모리 사용을 줄일 수 있다.
const VirtualizedOrderItem = ({
    order,
    onDetail,
    onCancel,
    onConfirmPurchase,
    onWriteReview,
}) => {
    const wrapperRef = useRef(null);
    const heightRef = useRef(280); // 측정 전 기본 placeholder 높이
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const node = wrapperRef.current;
        if (!node) return undefined;
        const observer = new IntersectionObserver(
            ([entry]) => setIsVisible(entry.isIntersecting),
            { rootMargin: "600px 0px" }
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    // 카드가 마운트되어 있을 때 실제 렌더링된 높이를 기록해 둔다.
    // 다음에 placeholder 로 바뀔 때 이 높이가 적용되어 스크롤 점프가 없도록 한다.
    useEffect(() => {
        if (isVisible && wrapperRef.current) {
            const measured = wrapperRef.current.offsetHeight;
            if (measured > 0) {
                heightRef.current = measured;
            }
        }
    }, [isVisible, order]);

    return (
        <div ref={wrapperRef} style={{ minHeight: heightRef.current }}>
            {isVisible && (
                <OrderCard
                    order={order}
                    onDetail={onDetail}
                    onCancel={onCancel}
                    onConfirmPurchase={onConfirmPurchase}
                    onWriteReview={onWriteReview}
                />
            )}
        </div>
    );
};

const OrderHistory = () => {
    const navigate = useNavigate();
    const { showSuccess, showError } = useSnackbar();
    const [orderHistory, setOrderHistory] = useState([]);
    const [nextPage, setNextPage] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [reviewOrder, setReviewOrder] = useState(null);
    const sentinelRef = useRef(null);

    const fetchOrderHistory = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await getMyOrderHistory(1);
            if (res) {
                setOrderHistory(res.results || []);
                setNextPage(extractPageFromUrl(res.next));
            } else {
                setOrderHistory([]);
                setNextPage(null);
            }
        } catch (error) {
            console.error("주문 내역 조회 오류:", error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const loadMore = useCallback(async () => {
        if (!nextPage || isLoadingMore) return;
        setIsLoadingMore(true);
        try {
            const res = await getMyOrderHistory(nextPage);
            if (res) {
                setOrderHistory((prev) => [...prev, ...(res.results || [])]);
                setNextPage(extractPageFromUrl(res.next));
            }
        } catch (error) {
            console.error("주문 내역 추가 조회 오류:", error);
        } finally {
            setIsLoadingMore(false);
        }
    }, [nextPage, isLoadingMore]);

    useEffect(() => {
        fetchOrderHistory();
    }, [fetchOrderHistory]);

    useRefreshCallback(() => {
        return getMyOrderHistory(1).then((res) => {
            if (res) {
                setOrderHistory(res.results || []);
                setNextPage(extractPageFromUrl(res.next));
            }
        });
    });

    // 무한 스크롤: 리스트 하단의 sentinel 이 viewport 에 들어오면 다음 페이지를 호출한다.
    useEffect(() => {
        const node = sentinelRef.current;
        if (!node) return undefined;
        if (!nextPage) return undefined;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) loadMore();
            },
            { rootMargin: "200px 0px" }
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, [nextPage, loadMore]);

    const handleOrderDetail = (orderId) => {
        navigate(`/order-history/${orderId}`);
    };

    const handleConfirmPurchase = async (orderId) => {
        if (!window.confirm("구매를 확정하시겠습니까?\n확정 후에는 취소할 수 없습니다.")) {
            return;
        }

        try {
            const res = await confirmPurchase(orderId);
            showSuccess(res.message || "구매가 확정되었습니다.");
            fetchOrderHistory();
        } catch (error) {
            const errorMessage =
                error.response?.data?.message || "구매 확정에 실패했습니다.";
            showError(errorMessage);
        }
    };

    const handleCancelOrder = async (orderId) => {
        if (!window.confirm("주문을 취소하시겠습니까?")) {
            return;
        }

        try {
            await cancelOrder(orderId);
            showSuccess("주문이 취소되었습니다.");
            fetchOrderHistory();
        } catch (error) {
            console.error("주문 취소 오류:", error);
            showError("주문 취소에 실패했습니다.");
        }
    };

    if (isLoading) {
        return (
            <div className="order-history">
                <SubHeader title="주문/배송 내역" />
                <LoadingSpinner />
            </div>
        );
    }

    return (
        <div className="order-history">
            <SubHeader title="주문/배송 내역" />
            <div className="order-history-content">
                {orderHistory.length === 0 ? (
                    <div className="empty-state">
                        <i className="bi bi-box-seam"></i>
                        <p>주문 내역이 없습니다</p>
                    </div>
                ) : (
                    <div className="order-list">
                        {orderHistory.map((order) => (
                            <VirtualizedOrderItem
                                key={order.id}
                                order={order}
                                onDetail={handleOrderDetail}
                                onCancel={handleCancelOrder}
                                onConfirmPurchase={handleConfirmPurchase}
                                onWriteReview={(o) => setReviewOrder(o)}
                            />
                        ))}
                        <div ref={sentinelRef} style={{ height: 1 }} />
                        {isLoadingMore && (
                            <div className="order-history__loading-more">
                                <LoadingSpinner />
                            </div>
                        )}
                    </div>
                )}
            </div>
            <ReviewWriteBottomSheet
                isOpen={!!reviewOrder}
                onClose={() => setReviewOrder(null)}
                order={reviewOrder}
                onSuccess={fetchOrderHistory}
            />
        </div>
    );
};

export default OrderHistory;
